import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { LazyMonacoEditor } from '@/components/lazy-monaco-editor'
import { Switch } from '@/components/ui/switch'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  ChevronRight,
  ChevronLeft,
  Edit2,
  X,
  Microchip,
  Fan,
  Disc3,
  ChevronDown,
  Lock,
  HardDrive,
  Check,
  Globe,
  Shield,
  LockKeyhole,
  Edit3,
  Settings,
  Terminal,
} from 'lucide-react'
import { useListServerTemplates } from '@/services/template'
import { useCreateServerInstance } from '@/services/server'
import { useListStorageVolumes } from '@/services/storage'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { useTokenAndOrgId } from '@/services/utils'
import { useNavigate } from '@tanstack/react-router'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { toast } from 'react-hot-toast'
import { useGetSSHKeys } from '@/services/sshkeys'
import { LoadingMessage } from '@/components/loading-message'
import { cn } from '@/lib/utils'
import { useUserStore } from '@/store/UserStore'
import { SetupSSHKeysDialog, SetupSSHKeysDialogRef } from './SetupSSHKeysDialog'
import {
  SetupDockerCredsDialog,
  SetupDockerCredsDialogRef,
} from './SetupDockerCredsDialog'
import { useTheme } from '@/components/theme-provider'
import { SetupStorageDialog } from '../DashboardStorage/SetupStorageDialog'
import { useCurrentUserOrgLimit } from '@/hooks/useCurrentUserOrgLimit'
import { SpecCard } from '@/components/spec-card'
import { AutoscalingConfigForm } from '@/components/autoscaling-config-form'
import {
  TemplateResponseData,
  CreateServerInstanceRequest,
  ListStorageVolumeDataItem,
} from '@/services/inference.gen'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { TemplateSelection } from './TemplateSelection'
import { PortManager } from './PortManager'
import { EnvVarsManager } from './EnvVarsManager'
import { StorageTypeId } from '@/constants/storageTypes'
import { useListDockerCreds } from '@/services/dockerCreds'
import { Container } from 'lucide-react'

const formSchema = z.object({
  serverName: z
    .string()
    .min(2, 'Server name must be at least 2 characters')
    .max(100, 'Server name must be less than 100 characters')
    .refine((val) => !/^\d+$/.test(val), 'Server name cannot be only numbers'),
  sshAccess: z.boolean(),
  storageVolumeId: z.string().nullable().optional(),
  httpPorts: z.array(z.string().regex(/^\d+$/, 'Must be a valid port number')),
  enabledScale: z.boolean(),
  pollingInterval: z.coerce.number().min(1),
  cooldownPeriod: z.coerce.number().min(1),
  minReplicaCount: z.coerce.number().min(1),
  maxReplicaCount: z.coerce.number().min(1),
  envVars: z.record(z.string(), z.string()),
  commandVars: z.record(z.string(), z.string()),
  customCommands: z.array(z.string()),
  overrideDockerImage: z.string().optional(),
  overrideDockerCredId: z.string().nullable().optional(),
})

export type DashboardNewServerFormData = z.infer<typeof formSchema>

// Alias for internal usage to maintain existing references
type FormData = DashboardNewServerFormData

export const DashboardNewServer = () => {
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(true)
  const { theme } = useTheme()
  const { setHasCreatedFirstServer } = useUserStore((s) => ({
    setHasCreatedFirstServer: s.setHasCreatedFirstServer,
  }))
  const [step, setStep] = useState(0)
  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateResponseData | null>(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false)

  const { orgId } = useTokenAndOrgId()
  const navigate = useNavigate()
  const userLimit = useCurrentUserOrgLimit()
  const serverInstanceLimit = userLimit
    ? userLimit.org_per_user_server_count
    : 0

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serverName: '',
      sshAccess: false,
      storageVolumeId: undefined,
      httpPorts: [],
      enabledScale: false,
      pollingInterval: 30,
      cooldownPeriod: 300,
      minReplicaCount: 1,
      maxReplicaCount: 5,
      envVars: {},
      commandVars: {},
      customCommands: [],
      overrideDockerImage: '',
      overrideDockerCredId: undefined,
    },
  })

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isErrorTemplates,
  } = useListServerTemplates()
  const { data: volumeData } = useListStorageVolumes({})
  const { data: sshKeys } = useGetSSHKeys()
  const { data: dockerCreds } = useListDockerCreds()
  const volumes = useMemo(
    () => (volumeData?.allVolumes as ListStorageVolumeDataItem[]) || [],
    [volumeData?.allVolumes]
  )
  
  // Check if Longhorn or Local volume is compatible with selected template
  const isVolumeCompatible = useCallback((volume: ListStorageVolumeDataItem) => {
    if (!selectedTemplate) return true
    
    // Check compatibility for Longhorn and Local volumes (both require cluster matching)
    if (volume.storage_volume.type_id === StorageTypeId.LONGHORN || 
        volume.storage_volume.type_id === StorageTypeId.LOCAL) {
      const volumeKubeConfId = volume.storage_volume.storage_volume_kube?.[0]?.kube_conf_id
      return !volumeKubeConfId || volumeKubeConfId === selectedTemplate.kube_conf_id
    }
    
    return true
  }, [selectedTemplate])

  const handleTemplateSelect = useCallback(
    (template: TemplateResponseData) => {
      setSelectedTemplate(template)
      form.setValue('serverName', template.template_name)
      form.setValue(
        'sshAccess',
        template.ssh_required || template.support_ssh || false
      )
    },
    [form]
  )

  useEffect(() => {
    if (
      !isLoadingTemplates &&
      templates &&
      templates.length > 0 &&
      !selectedTemplate
    ) {
      handleTemplateSelect(templates[0])
    }
  }, [templates, isLoadingTemplates, handleTemplateSelect, selectedTemplate])
  
  // Clear incompatible volume selection when template changes
  useEffect(() => {
    const currentVolumeId = form.getValues('storageVolumeId')
    if (currentVolumeId && selectedTemplate) {
      const selectedVolume = volumes.find(v => String(v.storage_volume.volume_id) === currentVolumeId)
      if (selectedVolume && !isVolumeCompatible(selectedVolume)) {
        form.setValue('storageVolumeId', undefined)
        toast('Storage volume deselected: incompatible with selected template', { icon: '⚠️' })
      }
    }
  }, [selectedTemplate, form, volumes, isVolumeCompatible])

  const handleNext = () => selectedTemplate && setStep(1)
  const handleBack = () => setStep(0)

  const handleNameChange = async () => {
    if (selectedTemplate) {
      const isValid = await form.trigger('serverName')
      if (isValid) {
        const newName = form.getValues('serverName')
        setSelectedTemplate({ ...selectedTemplate, template_name: newName })
        setIsEditingName(false)
      }
    }
  }

  const handleServerNameFieldFocus = () => {
    const focusInput = () => {
      const input = document.querySelector(
        '[data-testid="server-name-input"]'
      ) as HTMLInputElement
      if (input) {
        input.focus()
        input.select()
      }
    }

    if (!isEditingName) {
      setIsEditingName(true)
      if (selectedTemplate) {
        form.setValue('serverName', selectedTemplate.template_name)
      }
      setTimeout(focusInput, 100)
    } else {
      setTimeout(focusInput, 0)
    }
  }

  const createServerMutation = useCreateServerInstance()

  const onSubmit = (values: FormData) => {
    if (!selectedTemplate || !orgId) return

    // determine if user provided a custom init command
    const hasCustomCmd =
      values.customCommands.length > 0 &&
      values.customCommands[0] &&
      values.customCommands[0].trim() !== ''

    // build the custom command array only when needed; otherwise keep it empty
    const customCmds = hasCustomCmd
      ? selectedTemplate.init_commands &&
        selectedTemplate.init_commands.command[1] === '-c'
        ? [
            selectedTemplate.init_commands.command[0],
            selectedTemplate.init_commands.command[1],
            values.customCommands[0],
          ]
        : [values.customCommands[0]]
      : undefined

    const requestData: Omit<CreateServerInstanceRequest, 'org_id'> = {
      template_id: selectedTemplate.template_id,
      instance_name: values.serverName.trim(),
      storage_volume_id: values.storageVolumeId
        ? parseInt(values.storageVolumeId, 10)
        : 0,
      storage_volume_ids: [],
      allow_ssh: values.sshAccess,
      extra_ports: values.httpPorts.map(Number),
      enabled_scale: selectedTemplate.support_scale
        ? values.enabledScale
        : false,
      instance_scale_config:
        selectedTemplate.support_scale && values.enabledScale
          ? {
              polling_interval: values.pollingInterval,
              cooldown_period: values.cooldownPeriod,
              min_replica_count: values.minReplicaCount,
              max_replica_count: values.maxReplicaCount,
            }
          : undefined,
      env_var: values.envVars,
      command_vars: values.commandVars,
      custom_commands: customCmds,
      override_docker_image: values.overrideDockerImage?.trim() || undefined,
      // Only send credential if we're also overriding the image
      override_docker_cred_id:
        values.overrideDockerImage?.trim() && values.overrideDockerCredId
          ? parseInt(values.overrideDockerCredId, 10)
          : undefined,
    }

    createServerMutation.mutate(requestData, {
      onSuccess: () => {
        setHasCreatedFirstServer()
        navigate({ to: `/${orgId}/dashboard/servers` })
      },
      onError: (error) => {
        console.error(error)
        toast.error(JSON.stringify(error))
      },
    })
  }

  const sshDialogRef = React.useRef<SetupSSHKeysDialogRef>(null)
  const dockerCredsDialogRef = React.useRef<SetupDockerCredsDialogRef>(null)

  return (
    <Form {...form}>
      <form className="w-full mx-auto" onSubmit={(e) => e.preventDefault()}>
        {step === 0 && (
          <TemplateSelection
            templates={templates}
            isLoading={isLoadingTemplates}
            isError={isErrorTemplates}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={handleTemplateSelect}
            onNext={handleNext}
          />
        )}

        {step === 1 && selectedTemplate && (
          <div>
            <h1
              className="text-3xl font-medum mb-6"
              data-testid="configuration-title"
            >
              Edit Configurations
            </h1>
            <Card data-testid="configuration-card">
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  <span className="flex items-center text-2xl">
                    {!isEditingName ? (
                      <>
                        <span data-testid="server-name-display">
                          {selectedTemplate.template_name}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="ml-2"
                          data-testid="edit-server-name-button"
                          onClick={() => {
                            setIsEditingName(true)
                            form.setValue(
                              'serverName',
                              selectedTemplate.template_name
                            )
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <div className="flex flex-col">
                        <FormField
                          control={form.control}
                          name="serverName"
                          render={({ field }) => (
                            <FormItem className="space-y-2">
                              <div className="flex items-center">
                                <FormControl>
                                  <Input
                                    placeholder="Enter Server name"
                                    maxLength={50}
                                    autoFocus
                                    data-testid="server-name-input"
                                    className="text-2xl h-auto py-1 font-normal"
                                    {...field}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleNameChange()
                                      else if (e.key === 'Escape')
                                        setIsEditingName(false)
                                    }}
                                  />
                                </FormControl>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="ml-2"
                                  data-testid="confirm-server-name-button"
                                  onClick={handleNameChange}
                                >
                                  <Check className="h-4 w-4 text-green-500" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  data-testid="cancel-server-name-button"
                                  onClick={() => setIsEditingName(false)}
                                >
                                  <X className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </span>
                  <div className="flex flex-col items-end">
                    <Badge
                      variant="secondary"
                      className="text-lg font-bold px-3"
                    >
                      ${parseFloat((selectedTemplate.price / 100).toFixed(2))}
                    </Badge>
                    <span className="text-xs text-muted-foreground mt-1">
                      per hour
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Hardware Specs */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-muted-foreground">
                    Specifications
                  </h3>
                  <div
                    className={cn(
                      'grid gap-4',
                      'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'
                    )}
                  >
                    <SpecCard
                      icon={<Fan className="h-5 w-5" />}
                      title="GPU"
                      items={[
                        {
                          label: 'Count',
                          value: `${selectedTemplate.gpu_request_count}x`,
                        },
                        {
                          label: 'VRAM',
                          value: `${selectedTemplate.gpu_request_gmem} GB`,
                        },
                      ]}
                      variant="gpu"
                      minHeight="min-h-[80px]"
                    />
                    <SpecCard
                      icon={<Microchip className="h-5 w-5" />}
                      title="CPU"
                      items={[
                        {
                          label: 'Count',
                          value: `${selectedTemplate.cpu_limit_count}x`,
                        },
                      ]}
                      minHeight="min-h-[80px]"
                    />
                    <SpecCard
                      icon={<Microchip className="h-5 w-5 opacity-50" />}
                      title="Memory"
                      items={[
                        {
                          label: 'RAM',
                          value: `${selectedTemplate.ram_limit_gb} GB`,
                        },
                      ]}
                      minHeight="min-h-[80px]"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-3 bg-muted/30 rounded p-3">
                    <Disc3 className="h-5 w-5 text-primary" />
                    <span className="text-sm font-medium">Machine Image:</span>
                    <span className="text-sm text-muted-foreground">
                      {selectedTemplate.machine_image}
                    </span>
                  </div>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 mt-3 bg-muted/30 rounded p-3 cursor-help">
                          <HardDrive className="h-5 w-5 text-primary" />
                          <span className="text-sm font-medium">Storage:</span>
                          <span className="text-sm text-muted-foreground">
                            {selectedTemplate.ephemeral_storage_limit_gb} GB
                            (Ephemeral)
                          </span>
                          <InfoCircledIcon className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        className="max-w-[300px] p-3"
                        side="top"
                        align="start"
                      >
                        <p>
                          This storage is ephemeral, data will be deleted along
                          with the server if it is destroyed, if you want
                          permanent storage please create and attach a storage
                          volume
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="h-px bg-border/60 my-3"></div>

                {/* Advanced Options */}
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                    className={cn(
                      'w-full flex items-center justify-between py-3 px-4 rounded-lg border transition-all',
                      'hover:border-primary/50 hover:bg-muted/20 group',
                      showAdvancedOptions
                        ? 'bg-muted/30 border-primary/30'
                        : 'bg-muted/10 border-border/40'
                    )}
                    data-testid="advanced-options-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'rounded-md p-1.5 transition-colors',
                          showAdvancedOptions
                            ? 'bg-primary/20'
                            : 'bg-muted group-hover:bg-muted/80'
                        )}
                      >
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 transition-transform duration-200',
                            showAdvancedOptions
                              ? 'transform rotate-180 text-primary'
                              : 'text-muted-foreground'
                          )}
                        />
                      </div>
                      <h3 className="text-sm font-medium">Advanced Options</h3>
                    </div>
                    <span
                      className={cn(
                        'text-xs px-2 py-1 rounded-full transition-colors',
                        showAdvancedOptions
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted/50 text-muted-foreground group-hover:bg-muted/70 group-hover:text-foreground'
                      )}
                    >
                      {showAdvancedOptions ? 'Hide options' : 'Show options'}
                    </span>
                  </button>

                  {showAdvancedOptions && (
                    <Accordion
                      type="multiple"
                      defaultValue={['access-storage']}
                      className="space-y-4 animate-in fade-in-50 pt-2 mb-8"
                      data-testid="advanced-options-accordion"
                    >
                      {/* Access & Storage Section */}
                      <AccordionItem
                        value="access-storage"
                        className="border rounded-lg"
                      >
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center gap-2">
                            <div className="rounded-md p-1.5 bg-primary/10">
                              <Shield className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium">
                              Access & Storage
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                            {/* SSH Access Card */}
                            <div
                              className={cn(
                                'space-y-2 rounded-lg p-3 min-h-[90px] border transition-all',
                                'bg-gradient-to-br from-muted/90 to-muted/40 border-border/40'
                              )}
                              data-testid="ssh-access-card"
                            >
                              <div className="flex items-center justify-between border-b border-border/40 pb-2">
                                <div className="flex items-center gap-2">
                                  <div className="rounded-md p-1 bg-muted/90">
                                    <Lock className="h-5 w-5" />
                                  </div>
                                  <span className="text-sm font-semibold">
                                    SSH Access
                                  </span>
                                </div>
                                <Button
                                  variant="link"
                                  className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (sshDialogRef.current) {
                                      sshDialogRef.current.openDialog()
                                    }
                                  }}
                                  type="button"
                                  data-testid="manage-ssh-button"
                                >
                                  Manage
                                </Button>
                              </div>

                              <FormField
                                control={form.control}
                                name="sshAccess"
                                render={({ field }) => (
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between gap-3">
                                      <FormLabel className="text-xs text-muted-foreground m-0">
                                        Status
                                      </FormLabel>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium">
                                          {field.value ? 'Enabled' : 'Disabled'}
                                        </span>
                                        <FormControl>
                                          <Switch
                                            disabled={
                                              !!selectedTemplate?.ssh_required
                                            }
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                            data-testid="ssh-access-switch"
                                            className="data-[state=checked]:bg-primary"
                                          />
                                        </FormControl>
                                      </div>
                                    </div>

                                    {selectedTemplate?.ssh_required &&
                                      (!sshKeys || sshKeys.length === 0) && (
                                        <div className="rounded-md bg-yellow-50 dark:bg-yellow-950/50 p-3 mt-2 border border-yellow-200 dark:border-yellow-900/50">
                                          <div className="flex">
                                            <InfoCircledIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
                                            <p className="text-xs text-yellow-800 dark:text-yellow-300">
                                              SSH key required for this
                                              template. Please create an SSH key
                                              before deploying.
                                              <span className="ml-1">
                                                <SetupSSHKeysDialog
                                                  orgId={orgId}
                                                  isInWarning={true}
                                                />
                                              </span>
                                            </p>
                                          </div>
                                        </div>
                                      )}

                                    <div className="hidden">
                                      <SetupSSHKeysDialog
                                        ref={sshDialogRef}
                                        orgId={orgId}
                                      />
                                      <SetupDockerCredsDialog ref={dockerCredsDialogRef} />
                                    </div>
                                  </div>
                                )}
                              />
                            </div>

                            {/* Storage Volume Card */}
                            <div
                              className={cn(
                                'space-y-2 rounded-lg p-3 min-h-[90px] border transition-all',
                                'bg-gradient-to-br from-muted/90 to-muted/40 border-border/40'
                              )}
                              data-testid="storage-volume-card"
                            >
                              <div className="flex items-center justify-between border-b border-border/40 pb-2">
                                <div className="flex items-center gap-2">
                                  <div className="rounded-md p-1 bg-muted/90">
                                    <HardDrive className="h-5 w-5" />
                                  </div>
                                  <span className="text-sm font-semibold">
                                    Storage Volume
                                  </span>
                                </div>
                                <SetupStorageDialog
                                  orgId={orgId}
                                  customTrigger={
                                    <Button
                                      variant="link"
                                      className="text-xs font-medium text-primary hover:text-primary/80 transition-colors p-0 h-auto"
                                      data-testid="manage-storage-button"
                                    >
                                      Manage
                                    </Button>
                                  }
                                />
                              </div>

                              <FormField
                                control={form.control}
                                name="storageVolumeId"
                                render={({ field }) => (
                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between gap-3">
                                      <FormLabel className="text-xs text-muted-foreground m-0">
                                        Attached
                                      </FormLabel>
                                      <div className="w-full max-w-[170px]">
                                        <FormControl>
                                          <Select
                                            onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                                            value={field.value || 'none'}
                                            disabled={
                                              !volumes || volumes.length === 0
                                            }
                                          >
                                            <SelectTrigger
                                              className="h-7 text-xs bg-background border-border/50"
                                              data-testid="storage-volume-selector"
                                            >
                                              <SelectValue placeholder="Select..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="none">
                                                None
                                              </SelectItem>
                                              {volumes.map((volume) => {
                                                const isDisabled = !isVolumeCompatible(volume)
                                                return (
                                                  <SelectItem
                                                    className={cn("w-full", isDisabled && "opacity-50 cursor-not-allowed")}
                                                    key={volume.storage_volume.volume_id}
                                                    value={String(volume.storage_volume.volume_id)}
                                                    disabled={isDisabled}
                                                  >
                                                    <div className="flex items-center gap-2 w-full">
                                                      <div className="flex items-center gap-1">
                                                        <Tooltip>
                                                          <TooltipTrigger asChild>
                                                            {volume.storage_volume.is_public ? (
                                                              <Globe aria-label="Public" className="h-3.5 w-3.5 text-primary/80" />
                                                            ) : (
                                                              <Shield aria-label="Private" className="h-3.5 w-3.5 text-primary/80" />
                                                            )}
                                                          </TooltipTrigger>
                                                          <TooltipContent side="top" className="text-xs">
                                                            {volume.storage_volume.is_public ? 'Public Volume' : 'Private Volume'}
                                                          </TooltipContent>
                                                        </Tooltip>
                                                        <Tooltip>
                                                          <TooltipTrigger asChild>
                                                            {volume.storage_volume.read_only ? (
                                                              <LockKeyhole aria-label="Read Only" className="h-3 w-3 text-primary/60" />
                                                            ) : (
                                                              <Edit3 aria-label="Read Write" className="h-3 w-3 text-primary/60" />
                                                            )}
                                                          </TooltipTrigger>
                                                          <TooltipContent side="top" className="text-xs">
                                                            {volume.storage_volume.read_only ? 'Read Only' : 'Read & Write'}
                                                          </TooltipContent>
                                                        </Tooltip>
                                                      </div>
                                                      <span className="flex-1">
                                                        {volume.storage_volume.volume_name} ({volume.storage_volume.size_gb} GB)
                                                        {isDisabled && ' (incompatible)'}
                                                      </span>
                                                    </div>
                                                  </SelectItem>
                                                )
                                              })}
                                            </SelectContent>
                                          </Select>
                                        </FormControl>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              />
                            </div>

                            {/* HTTP Ports */}
                            <PortManager
                              form={form}
                              selectedTemplate={selectedTemplate}
                            />
                          </div>
                        </AccordionContent>
                      </AccordionItem>

                      {/* Autoscaling Section */}
                      {selectedTemplate?.support_scale && (
                        <AccordionItem
                          value="autoscaling"
                          className="border rounded-lg"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:no-underline">
                            <div className="flex items-center gap-2">
                              <div className="rounded-md p-1.5 bg-primary/10">
                                <Settings className="h-4 w-4 text-primary" />
                              </div>
                              <span className="font-medium">Autoscaling</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <AutoscalingConfigForm
                              form={form}
                              isEnabled={form.watch('enabledScale')}
                              data-testid="autoscaling-card"
                            />
                          </AccordionContent>
                        </AccordionItem>
                      )}

                      {/* Overrides Section */}
                      <AccordionItem
                        value="overrides"
                        className="border rounded-lg"
                      >
                        <AccordionTrigger className="px-4 py-3 hover:no-underline">
                          <div className="flex items-center gap-2">
                            <div className="rounded-md p-1.5 bg-primary/10">
                              <Terminal className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium">Overrides</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                            {/* Environment Variables */}
                            <EnvVarsManager
                              form={form}
                              selectedTemplate={selectedTemplate}
                            />

                            {/* Docker Image Override Card */}
                            <div
                              className={cn(
                                'space-y-3 rounded-lg p-3 min-h-[90px] border transition-all',
                                'bg-gradient-to-br from-muted/90 to-muted/40 border-border/40'
                              )}
                              data-testid="docker-override-card"
                            >
                              <div className="flex items-center justify-between border-b border-border/40 pb-2">
                                <div className="flex items-center gap-2">
                                  <div className="rounded-md p-1 bg-muted/90">
                                    <Container className="h-5 w-5" />
                                  </div>
                                  <span className="text-sm font-semibold">
                                    Docker Image
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="link"
                                    className="text-xs font-medium text-primary hover:text-primary/80 transition-colors p-0 h-auto"
                                    onClick={(e) => {
                                      e.preventDefault()
                                      dockerCredsDialogRef.current?.openDialog()
                                    }}
                                    type="button"
                                  >
                                    Manage Credentials
                                  </Button>
                                  {form.watch('overrideDockerImage') && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => {
                                        form.setValue('overrideDockerImage', '')
                                        form.setValue('overrideDockerCredId', undefined)
                                      }}
                                    >
                                      Reset
                                    </Button>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-3">
                                <div className="space-y-1.5">
                                  <FormLabel className="text-xs text-muted-foreground">
                                    Image URL
                                  </FormLabel>
                                  <FormField
                                    control={form.control}
                                    name="overrideDockerImage"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormControl>
                                          <Input
                                            placeholder={selectedTemplate?.machine_image || 'e.g. nginx:latest'}
                                            className="h-8 text-sm"
                                            {...field}
                                            data-testid="docker-image-input"
                                          />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    Default: {selectedTemplate?.machine_image}
                                  </p>
                                </div>

                                {form.watch('overrideDockerImage') && (
                                  <div className="space-y-1.5">
                                    <FormLabel className="text-xs text-muted-foreground">
                                      Docker Credentials (optional)
                                    </FormLabel>
                                    <FormField
                                      control={form.control}
                                      name="overrideDockerCredId"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormControl>
                                            <Select
                                              onValueChange={(value) =>
                                                field.onChange(value === 'none' ? null : value)
                                              }
                                              value={field.value || 'none'}
                                            >
                                              <SelectTrigger
                                                className="h-8 text-sm"
                                                data-testid="docker-cred-selector"
                                              >
                                                <SelectValue placeholder="Select credentials..." />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">None (public image)</SelectItem>
                                                {dockerCreds?.map((cred) => (
                                                  <SelectItem key={cred.id} value={String(cred.id)}>
                                                    {cred.name} ({cred.docker_username})
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </FormControl>
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Command Variables Card */}
                            {selectedTemplate?.support_init_command_override &&
                              selectedTemplate?.init_command_override
                                ?.var_replace &&
                              selectedTemplate.init_command_override.var_replace
                                .length > 0 && (
                                <div
                                  className={cn(
                                    'space-y-2 rounded-lg p-3 min-h-[90px] border transition-all',
                                    'bg-gradient-to-br from-muted/90 to-muted/40 border-border/40'
                                  )}
                                  data-testid="command-overrides-card"
                                >
                                  <div className="flex items-center gap-2 border-b border-border/40 pb-2">
                                    <div className="rounded-md p-1 bg-muted/90">
                                      <Terminal className="h-5 w-5" />
                                    </div>
                                    <span className="text-sm font-semibold">
                                      Command Variables
                                    </span>
                                  </div>

                                  <div className="space-y-2">
                                    {selectedTemplate.init_command_override.var_replace.map(
                                      (varItem) => {
                                        const varKey = varItem.name
                                        const overrideValue =
                                          form.watch('commandVars')[varKey]
                                        const isOverridden =
                                          overrideValue !== undefined

                                        return (
                                          <div
                                            key={varKey}
                                            className="space-y-2"
                                          >
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm font-medium text-foreground">
                                                {`\${${varKey}}`}
                                              </span>
                                              <div
                                                className={cn(
                                                  'px-1.5 py-0.5 rounded text-xs font-medium',
                                                  isOverridden
                                                    ? 'bg-primary/20 text-primary'
                                                    : 'bg-muted text-muted-foreground'
                                                )}
                                              >
                                                {isOverridden
                                                  ? 'Custom'
                                                  : 'Default'}
                                              </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                              <Input
                                                value={overrideValue || ''}
                                                onChange={(e) => {
                                                  const newValue =
                                                    e.target.value
                                                  const currentCommandVars =
                                                    form.getValues(
                                                      'commandVars'
                                                    )

                                                  if (newValue.trim() === '') {
                                                    delete currentCommandVars[
                                                      varKey
                                                    ]
                                                    form.setValue(
                                                      'commandVars',
                                                      { ...currentCommandVars }
                                                    )
                                                  } else {
                                                    form.setValue(
                                                      'commandVars',
                                                      {
                                                        ...currentCommandVars,
                                                        [varKey]:
                                                          newValue.trim(),
                                                      }
                                                    )
                                                  }
                                                }}
                                                placeholder={`Default: ${varItem.default}`}
                                                className={cn(
                                                  'h-8 text-sm flex-1',
                                                  isOverridden
                                                    ? 'border-primary/50 bg-primary/5'
                                                    : 'border-border/50'
                                                )}
                                                data-testid={`command-override-${varKey}`}
                                              />

                                              {isOverridden && (
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 px-2 text-xs"
                                                  onClick={() => {
                                                    const currentCommandVars =
                                                      form.getValues(
                                                        'commandVars'
                                                      )
                                                    delete currentCommandVars[
                                                      varKey
                                                    ]
                                                    form.setValue(
                                                      'commandVars',
                                                      { ...currentCommandVars }
                                                    )
                                                  }}
                                                  data-testid={`reset-command-override-${varKey}`}
                                                >
                                                  Reset
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      }
                                    )}
                                  </div>
                                </div>
                              )}

                            {/* Custom Commands Card */}
                            <div
                              className={cn(
                                'space-y-4 rounded-lg p-4 min-h-[90px] border transition-all col-span-full',
                                'bg-gradient-to-br from-muted/90 to-muted/40 border-border/40'
                              )}
                              data-testid="custom-commands-card"
                            >
                              <div className="flex items-center gap-2 border-b border-border/40 pb-3">
                                <div className="rounded-md p-1 bg-muted/90">
                                  <Terminal className="h-5 w-5" />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-sm font-semibold">
                                    Command Override
                                  </span>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <InfoCircledIcon className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="top"
                                        className="max-w-[300px]"
                                      >
                                        <p className="text-sm">
                                          Custom initialization command executed
                                          when the server environment starts.
                                          Overrides the template's default
                                          startup command.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </div>

                              <div className="space-y-4">
                                <div className="space-y-3">
                                  <label className="text-sm font-medium block">
                                    init command:
                                  </label>
                                  <div className="border overflow-hidden">
                                    <LazyMonacoEditor
                                      height="120px"
                                      defaultLanguage="shell"
                                      value={
                                        form.watch('customCommands')[0] ||
                                        selectedTemplate?.init_commands
                                          ?.command?.[2] ||
                                        ''
                                      }
                                      onChange={(value) => {
                                        const currentValue = value || ''
                                        const defaultCommand =
                                          selectedTemplate?.init_commands
                                            ?.command?.[2] || ''

                                        if (
                                          currentValue === '' ||
                                          currentValue === defaultCommand
                                        ) {
                                          form.setValue('customCommands', [])
                                        } else {
                                          form.setValue('customCommands', [
                                            currentValue,
                                          ])
                                        }
                                      }}
                                      theme={
                                        theme === 'dark' ? 'vs-dark' : 'vs'
                                      }
                                      options={{
                                        minimap: { enabled: false },
                                        scrollBeyondLastLine: false,
                                        automaticLayout: true,
                                        lineNumbers: 'on',
                                        folding: false,
                                        lineDecorationsWidth: 10,
                                        lineNumbersMinChars: 2,
                                        fontSize: 12,
                                        fontFamily:
                                          'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
                                        wordWrap: 'on',
                                        scrollbar: {
                                          vertical: 'auto',
                                          horizontal: 'auto',
                                          verticalScrollbarSize: 8,
                                          horizontalScrollbarSize: 8,
                                        },
                                        padding: { top: 8, bottom: 8 },
                                        bracketPairColorization: {
                                          enabled: true,
                                        },
                                        suggest: {
                                          showKeywords: true,
                                          showSnippets: true,
                                        },
                                      }}
                                      data-testid="custom-command-editor"
                                    />
                                  </div>

                                  <div className="flex items-center justify-between pt-1">
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={cn(
                                          'px-3 py-1.5 rounded text-xs font-medium',
                                          form.watch('customCommands').length >
                                            0 &&
                                            form.watch('customCommands')[0] !==
                                              selectedTemplate?.init_commands
                                                ?.command?.[2]
                                            ? 'bg-primary/20 text-primary border border-primary/30'
                                            : 'bg-muted text-muted-foreground border border-border/30'
                                        )}
                                      >
                                        {form.watch('customCommands').length >
                                          0 &&
                                        form.watch('customCommands')[0] !==
                                          selectedTemplate?.init_commands
                                            ?.command?.[2]
                                          ? 'Custom Override'
                                          : 'Using Default'}
                                      </div>

                                      {form.watch('customCommands').length >
                                        0 &&
                                        form.watch('customCommands')[0] !==
                                          selectedTemplate?.init_commands?.command?.join(
                                            '\n'
                                          ) && (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-3 text-xs"
                                            onClick={() =>
                                              form.setValue(
                                                'customCommands',
                                                []
                                              )
                                            }
                                            data-testid="reset-custom-command-button"
                                          >
                                            Reset to Default
                                          </Button>
                                        )}
                                    </div>
                                  </div>

                                  <p className="text-xs text-muted-foreground mt-2">
                                    Also see:{' '}
                                    <a
                                      href="https://www.shellcheck.net/"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="underline hover:text-primary transition-colors"
                                    >
                                      ShellCheck
                                    </a>{' '}
                                    for command validation.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Configuration Sticky Footer */}
            <div className="sticky bottom-5 mt-8 pb-2 z-20">
              <div className="flex justify-start pointer-events-none">
                <div className="pointer-events-auto flex items-center bg-card border rounded-lg shadow-lg p-4 animate-in fade-in slide-in-from-bottom-5">
                  <Button
                    type="button"
                    onClick={handleBack}
                    variant="outline"
                    size="sm"
                    className="mr-3"
                    data-testid="back-button"
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" /> Back
                  </Button>

                  <div className="h-8 w-px bg-border mx-2"></div>

                  <div className="mx-3">
                    <div className="text-sm text-muted-foreground mb-1">
                      Ready to Deploy
                    </div>
                    <div className="font-medium">
                      {selectedTemplate.template_name}
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={async () => {
                      const isValid = await form.trigger()
                      if (isValid) {
                        setIsConfirmationDialogOpen(true)
                      } else {
                        const errors = form.formState.errors
                        const errorFields = Object.keys(errors)

                        if (errorFields.length > 0) {
                          const firstErrorKey = errorFields[0] as keyof FormData
                          const firstError = errors[firstErrorKey]

                          if (firstError) {
                            const fieldLabels: Record<string, string> = {
                              serverName: 'Server Name',
                              region: 'Region',
                              template: 'Template',
                            }

                            const fieldLabel =
                              fieldLabels[firstErrorKey] ||
                              firstErrorKey.charAt(0).toUpperCase() +
                                firstErrorKey.slice(1)
                            const errorMessage =
                              typeof firstError.message === 'string'
                                ? firstError.message
                                : `Invalid value for ${fieldLabel}.`

                            toast.error(errorMessage)

                            // Focus on the first invalid field
                            if (firstErrorKey === 'serverName') {
                              handleServerNameFieldFocus()
                            }
                          }
                        } else {
                          toast.error(
                            'Please check your configuration and try again'
                          )
                        }
                      }
                    }}
                    className="ml-2"
                    disabled={
                      (selectedTemplate?.ssh_required &&
                        (!sshKeys || sshKeys.length === 0)) ||
                      createServerMutation.isPending
                    }
                    data-testid="deploy-server-button"
                  >
                    {createServerMutation.isPending ? (
                      <>
                        <LoadingMessage size="sm" /> Deploying...
                      </>
                    ) : (
                      <>
                        Deploy Server <ChevronRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <Dialog
          open={isConfirmationDialogOpen}
          onOpenChange={setIsConfirmationDialogOpen}
          data-testid="confirmation-dialog"
        >
          <DialogContent data-testid="confirmation-dialog-content">
            <DialogHeader>
              <DialogTitle data-testid="confirmation-dialog-title">
                Confirm Server Deployment
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p>
                Are you sure you want to deploy this server with the selected
                configurations?
              </p>
            </div>
            <Alert variant="warning">
              <InfoCircledIcon className="h-4 w-4" />
              <AlertTitle>Organization Limits:</AlertTitle>
              <AlertDescription>
                {serverInstanceLimit} active server instances
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setIsConfirmationDialogOpen(false)}
                disabled={createServerMutation.isPending}
                data-testid="cancel-deploy-button"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setIsConfirmationDialogOpen(false)
                  form.handleSubmit(onSubmit)()
                }}
                disabled={createServerMutation.isPending}
                data-testid="confirm-deploy-button"
              >
                {createServerMutation.isPending ? (
                  <>
                    <LoadingMessage size="sm" /> Deploying...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </form>
    </Form>
  )
}
